import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useClientId } from '../../../../../../_hooks/useClientId';
import {
  hasAccessToEditProfileSelector,
  canEditLinkedAccountOwnerAndSyncSelector,
} from '../../../../../../CommonSelectors/CommonAreaSelector';
import { ACCOUNTS_QUERY_KEY } from '../../../../AccountsConstants';
import { useAccountBeneficiariesMutation } from '../../../../api/useAccountBeneficiariesMutation';
import { useAccountPortfolioMutation } from '../../../../api/useAccountPortfolioMutation';
import { AccountPortfolioDTO } from '../../../../api/useAccountPortfolioQuery';
import { useAccountSaveMutation } from '../../../../api/useAccountSaveMutation';
import {
  ACCOUNT_OPTIONS_QUERY_KEY,
  DistributionStrategyTypeDTO,
} from '../../../../api/useAccountsOptionsQuery';
import { ACCOUNT_PREFERENCES_QUERY_KEY, OwnerTypeDTO } from '../../../../api/useAccountsQuery';
import { useAccountFormContext } from './context/useAccountFormContext';
import {
  accountPortfolioDataToForm,
  accountPortfolioFormToBody,
  ACCOUNTS_WITH_BENEFICIARIES_TAB,
  bankAccountBeneficiariesFormToBeneficiariesBody,
  bankAccountsFormToBody,
  buildLinkedAccountLimitedSaveFormFields,
  validation,
  dataToForm,
  defaultFormValues,
  FormFields,
  getAccountHasAssetAllocationTab,
  LocalSaveOptions,
} from './AccountForm.meta';
import { queryKeys as pulseCheckQueryKeys } from '../../../../../../PulseCheck/api/queryKeys';
import { queryKeys as charitableGivingWorkflowQueryKeys } from '../../../../../../CharitableGivingWorkflow/stages/CharitableGiving/api/queryKeys';
import { AccountModalUsageContext } from './context/AccountFormProvider.meta';

interface Params {
  accountId?: string;
  accountPortfolioData?: AccountPortfolioDTO;
  localSaveOptions?: LocalSaveOptions;
}

export const useAccountForm = ({ accountId, accountPortfolioData }: Params) => {
  const { accounts, options, usageContext, localSaveOptions } = useAccountFormContext();

  const hasEditProfileAccess = useSelector(hasAccessToEditProfileSelector);
  const canEditLinkedAccountOwnerAndSync = useSelector(canEditLinkedAccountOwnerAndSyncSelector);

  const queryClient = useQueryClient();

  const clientId = useClientId();

  const {
    error: saveAccountErrors,
    isLoading: isSavingAccount,
    mutateAsync: saveBankAccounts,
  } = useAccountSaveMutation();

  const {
    error: saveAccountPortfolioErrors,
    isLoading: isSavingAccountPortfolio,
    mutateAsync: saveAccountPortfolio,
  } = useAccountPortfolioMutation();

  const {
    error: saveAccountBeneficiariesErrors,
    isLoading: isSavingAccountBeneficiaries,
    mutateAsync: saveAccountBeneficiaries,
  } = useAccountBeneficiariesMutation();

  const selectedAccount = localSaveOptions
    ? localSaveOptions.data?.bankAccount
    : accounts.find(({ AccountId }) => AccountId === accountId);

  const accountPortfolio = localSaveOptions
    ? localSaveOptions.data?.accountPortfolio
    : accountPortfolioData;

  const {
    IndividualOwnerOptions,
    ContributionStartYearOptions,
    ContributionEndYearOptions,
    CustomDistributionStrategyOptions,
    BeneficiariesOptions: beneficiariesOptions,
  } = options;

  const bankAccountDefaultValues = selectedAccount
    ? dataToForm(selectedAccount, options)
    : {
        ...defaultFormValues,
        AccountOwner: {
          label: IndividualOwnerOptions.People[0].Name,
          value: IndividualOwnerOptions.People[0].Name,
        },
        ContributionStartYear: ContributionStartYearOptions[0],
        ContributionEndYear: ContributionEndYearOptions[0],
        DistributionStrategyType: DistributionStrategyTypeDTO.None,
        DistributionStrategy: {
          ComputationYearType: CustomDistributionStrategyOptions.computationYearType[0].value,
          DistributionStartYearType: 0,
          DistributionEndYearType: 0,
          StartDistributionsToday: true,
          StartDistributionsAtRetirement: false,
          EndDistributionsAtRetirement: true,
          EndOfPlan: false,
          NoOtherDistributionsAllowed: false,
        },
        OwnerModel: [
          {
            Id: null,
            Name: IndividualOwnerOptions.People[0].Name,
            OwnerType: OwnerTypeDTO.Principal,
            Percentage: '100',
          },
        ],
      };

  const canEditOwnerAndSyncOnly =
    !localSaveOptions && !!selectedAccount?.IsLinked && canEditLinkedAccountOwnerAndSync;

  const formBag = useForm<FormFields>({
    defaultValues: {
      bankAccount: bankAccountDefaultValues,
      ...(accountPortfolio
        ? {
            accountPortfolio: accountPortfolioDataToForm({
              accountType: selectedAccount?.AccountType,
              accountPortfolio,
              accountOptions: options,
            }),
          }
        : {}),
    },
    resolver: yupResolver(validation(options)),

    disabled: !localSaveOptions && !hasEditProfileAccess,

    context: {
      allowAssetBucketingCustomization: options.AllowAssetBucketingCustomization,
      assetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
      showHypotheticalReturn: options.ShowHypotheticalReturn,
    },
  });

  const saveAccount = async (formData: FormFields) => {
    const data =
      canEditOwnerAndSyncOnly && selectedAccount
        ? buildLinkedAccountLimitedSaveFormFields(formBag.getValues(), selectedAccount, options)
        : formData;

    const response = await saveBankAccounts(bankAccountsFormToBody(data.bankAccount));

    if (response) {
      formBag.setValue('bankAccount.AccountId', response.AccountId);
    }

    if (canEditOwnerAndSyncOnly) {
      if (
        usageContext !== AccountModalUsageContext.CharitableGivingWorkflow &&
        usageContext !== AccountModalUsageContext.DafAccounts
      ) {
        await Promise.all([
          queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY, ACCOUNT_OPTIONS_QUERY_KEY, clientId]),
          queryClient.invalidateQueries([
            ACCOUNTS_QUERY_KEY,
            ACCOUNT_PREFERENCES_QUERY_KEY,
            clientId,
          ]),
          queryClient.invalidateQueries(pulseCheckQueryKeys.goalsOptionsAll()),
        ]);
      }

      return;
    }

    const accountPortfolioBody = accountPortfolioFormToBody(data.accountPortfolio);

    if (
      getAccountHasAssetAllocationTab(data.bankAccount.AccountType, {
        annuityType: data.bankAccount.AnnuityType,
      }) &&
      accountPortfolioBody &&
      response
    ) {
      await saveAccountPortfolio({
        ...accountPortfolioBody,
        AccountId: formBag.watch('bankAccount.AccountId') || response.AccountId,
      });
    }

    if (
      typeof data.bankAccount.AccountType === 'number' &&
      ACCOUNTS_WITH_BENEFICIARIES_TAB.includes(data.bankAccount.AccountType) &&
      data.bankAccount.AccountBeneficiaries &&
      response
    ) {
      await saveAccountBeneficiaries({
        ...bankAccountBeneficiariesFormToBeneficiariesBody(
          data.bankAccount.AccountBeneficiaries,
          beneficiariesOptions,
        ),
        AccountId: formBag.watch('bankAccount.AccountId') || response.AccountId,
      });
    }

    if (
      usageContext !== AccountModalUsageContext.CharitableGivingWorkflow &&
      usageContext !== AccountModalUsageContext.DafAccounts
    ) {
      await Promise.all([
        queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY, ACCOUNT_OPTIONS_QUERY_KEY, clientId]),
        queryClient.invalidateQueries([
          ACCOUNTS_QUERY_KEY,
          ACCOUNT_PREFERENCES_QUERY_KEY,
          clientId,
        ]),
        queryClient.invalidateQueries(pulseCheckQueryKeys.goalsOptionsAll()),
      ]);
    }

    if (usageContext === AccountModalUsageContext.DafAccounts) {
      queryClient.invalidateQueries(charitableGivingWorkflowQueryKeys.dafAccounts(clientId));
    }

    if (usageContext === AccountModalUsageContext.CharitableGivingWorkflow) {
      queryClient.invalidateQueries(charitableGivingWorkflowQueryKeys.accountsAll());
    }
  };

  return {
    data: {
      formBag,
      canEditOwnerAndSyncOnly,
      errors: saveAccountErrors || saveAccountPortfolioErrors || saveAccountBeneficiariesErrors,
      isSaving: isSavingAccount || isSavingAccountPortfolio || isSavingAccountBeneficiaries,
    },
    actions: {
      saveAccount,
    },
  };
};
